import {
    FloatingFocusManager,
    FloatingList,
    Placement,
    autoUpdate,
    flip,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListNavigation,
    useRole,
    useTypeahead,
} from "@floating-ui/react";
import classNames from "classnames";
import React, { PropsWithChildren, createContext, use } from "react";

import { omit } from "@utils/general";
import { isDefined } from "@utils/helpers";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "../Button/button.component";

type DropdownContextValue = {
    activeIndex: number | null;
    selectedIndex: number | null;
    getItemProps: ReturnType<typeof useInteractions>["getItemProps"];
    handleSelect: (index: number | null) => void;
};

type DropdownContextProviderProps = PropsWithChildren<{
    color?: string;
    placeholder?: React.ReactNode;
    className?: string;
    placement?: Placement;
    useLabel?: boolean;
    components?: {
        toggle?: Record<string, any>;
        menu?: Record<string, any>;
    };
}>;

export const DropdownContext = createContext<DropdownContextValue | undefined>(
    undefined,
);

DropdownContext.displayName = "DropdownContext";

export const Dropdown = ({
    children,
    color = "primary",
    className,
    placeholder,
    placement = "bottom-start",
    useLabel = true,
    components: { toggle, menu } = { toggle: { caret: true }, menu: {} },
}: DropdownContextProviderProps) => {
    const { t } = useGranite();
    const [isOpen, setIsOpen] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(
        null,
    );
    const [selectedLabel, setSelectedLabel] = React.useState<string | null>(
        null,
    );

    const { refs, floatingStyles, context } = useFloating({
        placement,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        middleware: [flip()],
    });

    const elementsRef = React.useRef<Array<HTMLElement | null>>([]);
    const labelsRef = React.useRef<Array<string | null>>([]);

    const handleSelect = React.useCallback((index: number | null) => {
        setSelectedIndex(index);
        setIsOpen(false);
        if (index !== null) {
            setSelectedLabel(labelsRef.current[index]);
        }
    }, []);

    function handleTypeaheadMatch(index: number | null) {
        if (isOpen) {
            setActiveIndex(index);
        } else {
            handleSelect(index);
        }
    }

    const listNav = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        selectedIndex,
        onNavigate: setActiveIndex,
    });
    const typeahead = useTypeahead(context, {
        listRef: labelsRef,
        activeIndex,
        selectedIndex,
        onMatch: handleTypeaheadMatch,
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "listbox" });

    const { getReferenceProps, getFloatingProps, getItemProps } =
        useInteractions([listNav, typeahead, click, dismiss, role]);

    const dropdownContext = React.useMemo(
        () => ({
            activeIndex,
            selectedIndex,
            getItemProps,
            handleSelect,
        }),
        [activeIndex, selectedIndex, getItemProps, handleSelect],
    );

    const classes = classNames(className, "dropdown");
    const toggleClasses = classNames(
        { "dropdown-toggle": toggle?.caret },
        `btn btn-${color}`,
        toggle?.className,
    );
    const menuclasses = classNames(
        "dropdown-menu",
        { show: isOpen },
        menu?.className,
    );

    return (
        <div className={classes}>
            <Button
                ref={refs.setReference}
                tabIndex={0}
                {...getReferenceProps()}
                className={toggleClasses}
                data-toggle="dropdown"
                {...omit(toggle || {}, ["className", "caret"])}
            >
                {(useLabel && selectedLabel) ||
                    placeholder ||
                    t("general.dropdown.placeholder")}
            </Button>
            <DropdownContext value={dropdownContext}>
                {isOpen && (
                    <FloatingFocusManager context={context} modal={false}>
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            {...getFloatingProps()}
                            className={menuclasses}
                            {...omit(menu || {}, ["className"])}
                        >
                            <FloatingList
                                elementsRef={elementsRef}
                                labelsRef={labelsRef}
                            >
                                {children}
                            </FloatingList>
                        </div>
                    </FloatingFocusManager>
                )}
            </DropdownContext>
        </div>
    );
};

export const useDropdownContext = () => {
    const context = use<DropdownContextValue | undefined>(
        DropdownContext,
    );

    if (!isDefined(context)) {
        throw new Error(`${DropdownContext.displayName} is undefined.`);
    }

    return context;
};
